import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, User, FolderOpen, Github, Cpu, Mail, Command, Clock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { personalInfo } from '../data/mock';

const navItems = [
  { path: '/', label: 'Ana Sayfa', Icon: Home },
  { path: '/about', label: 'Hakkımda', Icon: User },
  { path: '/projects', label: 'Projeler', Icon: FolderOpen },
  { path: '/repositories', label: 'Repolar', Icon: Github },
  { path: '/technologies', label: 'Teknolojiler', Icon: Cpu },
  { path: '/contact', label: 'İletişim', Icon: Mail },
];

const Header = () => {
  const location = useLocation();
  const { themeColor } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredNav, setHoveredNav] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Clock update
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'glass-strong'
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ delay: 2, duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <motion.span
                className="text-2xl font-bold"
                style={{ color: themeColor.primary }}
                whileHover={{ scale: 1.2, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                ✶
              </motion.span>
            </Link>

            {/* Desktop Navigation with Icons */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const isHovered = hoveredNav === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="relative px-4 py-2 text-sm font-medium rounded-xl transition-all"
                    onMouseEnter={() => setHoveredNav(item.path)}
                    onMouseLeave={() => setHoveredNav(null)}
                  >
                    <motion.span 
                      className="flex items-center gap-2"
                      animate={{ x: isHovered ? 3 : 0 }}
                    >
                      <motion.span
                        animate={{ 
                          scale: isHovered ? 1.15 : 1,
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <item.Icon
                          size={15}
                          style={{ color: isActive || isHovered ? themeColor.primary : 'rgb(161,161,170)' }}
                          strokeWidth={isActive ? 2.5 : 2}
                        />
                      </motion.span>
                      <span className={isActive ? 'text-white' : 'text-zinc-400 hover:text-white'}>
                        {item.label}
                      </span>
                    </motion.span>
                    {isActive && (
                      <motion.div
                        className="absolute inset-0 rounded-xl -z-10"
                        style={{ backgroundColor: `rgba(${themeColor.rgb}, 0.15)` }}
                        layoutId="navIndicator"
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                      />
                    )}
                    {isHovered && !isActive && (
                      <motion.div
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full"
                        style={{ backgroundColor: themeColor.primary }}
                        initial={{ width: 0 }}
                        animate={{ width: '50%' }}
                        exit={{ width: 0 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Modern Clock */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-2xl glass">
                <Clock size={14} style={{ color: themeColor.primary }} />
                <span className="text-sm font-mono text-zinc-400">
                  {formatTime(currentTime)}
                </span>
              </div>

              {/* Command Palette Trigger */}
              <button
                onClick={() => setShowCommandPalette(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-2xl glass text-zinc-500 hover:text-white transition-all text-sm"
              >
                <Command size={14} />
                <kbd className="px-1.5 py-0.5 text-xs bg-zinc-900 rounded border border-zinc-700">K</kbd>
              </button>

              {/* Instagram */}
              <a
                href="https://instagram.com/canervac"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all"
                data-testid="header-instagram-link"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>

              {/* Mobile Menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2.5 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-white"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              className="lg:hidden bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-zinc-800/50"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-white/5 text-white'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                      }`}
                    >
                      <item.Icon size={16} style={{ color: isActive ? themeColor.primary : 'rgb(161,161,170)' }} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Command Palette */}
      <AnimatePresence>
        {showCommandPalette && (
          <CommandPalette onClose={() => setShowCommandPalette(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

// Command Palette Component
const CommandPalette = ({ onClose }) => {
  const { themeColor } = useTheme();
  const [search, setSearch] = useState('');

  const filteredItems = navItems.filter(item =>
    item.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg bg-[#0a0a0a] border border-zinc-800 rounded-2xl shadow-2xl z-[101] overflow-hidden"
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
      >
        <div className="p-4 border-b border-zinc-800">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sayfaları ara..."
            className="w-full bg-transparent text-white placeholder-zinc-500 outline-none text-lg"
            autoFocus
          />
        </div>
        <div className="p-2 max-h-[300px] overflow-y-auto">
          {filteredItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all"
            >
              <span style={{ filter: 'grayscale(100%)' }}>{item.emoji}</span>
              <span>{item.label}</span>
            </Link>
          ))}
          {filteredItems.length === 0 && (
            <p className="text-center text-zinc-500 py-8">Sonuç bulunamadı</p>
          )}
        </div>
        <div className="p-3 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
          <span>Gezinmek için ↑↓ tuşlarını kullanın</span>
          <span>Kapatmak için ESC</span>
        </div>
      </motion.div>
    </>
  );
};

export default Header;
